import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { reducedMotion } from "../lib/fmt";

const DataCtx = createContext(null);
const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [fanfare, setFanfare] = useState(null);
  const toast = useCallback((msg, subtle = false) => {
    const id = Math.random();
    setToasts((t) => [...t, { id, msg, subtle }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  const celebrate = useCallback((icon) => {
    if (reducedMotion()) return;
    setFanfare({ icon, k: Math.random() });
    setTimeout(() => setFanfare(null), 900);
  }, []);
  return (
    <ToastCtx.Provider value={{ toast, celebrate }}>
      {children}
      <div aria-live="polite" className="toast-wrap" data-testid="toast-wrap">
        {toasts.map((t) => <div key={t.id} className={`toast ${t.subtle ? "subtle" : ""}`} data-testid="toast">{t.msg}</div>)}
      </div>
      {fanfare && <div key={fanfare.k} className="fanfare" aria-hidden="true">{fanfare.icon}</div>}
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

export function DataProvider({ children }) {
  const [dash, setDash] = useState(null);
  const [error, setError] = useState("");
  const { toast, celebrate } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/dashboard");
      setDash(data);
      setError("");
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = useCallback((res) => {
    if (res?.dashboard) setDash(res.dashboard);
    (res?.events || []).forEach((ev, i) => {
      setTimeout(() => {
        toast(ev.msg, ev.type === "achievement");
        if (ev.type === "level") celebrate("💎");
        if (ev.type === "chest") celebrate("🎁");
        if (ev.type === "streak") celebrate("🔥");
        if (ev.type === "achievement") celebrate("🏅");
      }, i * 650);
    });
  }, [toast, celebrate]);

  const run = useCallback(async (fn) => {
    try {
      const { data } = await fn();
      apply(data);
      return data;
    } catch (e) {
      toast(`⚠️ ${errMsg(e)}`, true);
      return null;
    }
  }, [apply, toast]);

  return <DataCtx.Provider value={{ d: dash, load, apply, run, error }}>{children}</DataCtx.Provider>;
}

export const useData = () => useContext(DataCtx);
